import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The lesson a teacher currently has open, remembered across route changes.
 *
 * WHY THIS EXISTS (Anthony's review, 2026-09-02 — card shSY0fRdd480eNH5S9iF):
 * *"when you are in a lesson, you're unable to do anything else on the site
 * without leaving the page."*
 *
 * THE TRAP IS NOT THE FULL-SCREEN TAKEOVER, which is what it looks like. At
 * >=1200px the dashboard already composes with the portal chrome; the takeover
 * (`.ld-shell { position: fixed }`) applies only below 1199px and is deliberate
 * — it exists to stop iOS rubber-banding the document under the fixed jot bar.
 * What actually traps a desktop teacher is the ROUTER: navigating unmounts the
 * lesson and takes the board, the unsent jot and the recap draft with it. So
 * the problem is state preservation, and this is the smaller half of the answer
 * — the half that makes leaving safe and coming back one tap.
 *
 * SESSION storage, not local: a lesson is open in ONE TAB. Keying it per-tab is
 * what makes a second browser window a legitimate multitasking answer — the
 * lesson stays "open" in its own tab and the other tab shows no phantom pill.
 * It also means closing the tab ends the session, which is correct.
 *
 * This holds a POINTER, never lesson content. Notes and the timer live in
 * Firestore; unsent scratch lives in useLessonScratch, keyed per lesson.
 */

const STORAGE_KEY = 'cyv.openLesson';

const LessonSessionContext = createContext({
	openLesson: null,
	setOpenLesson: () => {},
	clearOpenLesson: () => {},
});

const read = () => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed && parsed.lessonId ? parsed : null;
	} catch {
		// Private mode, blocked site data, or a corrupt value — a remembered
		// lesson is a convenience, never a correctness requirement.
		return null;
	}
};

export const LessonSessionProvider = ({ children }) => {
	const [openLesson, setOpenLessonState] = useState(read);

	useEffect(() => {
		try {
			if (openLesson) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(openLesson));
			else sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			/* see read() — storage is best-effort */
		}
	}, [openLesson]);

	const setOpenLesson = useCallback((lessonId, studentName = '') => {
		if (!lessonId) return;
		setOpenLessonState((prev) =>
			// Don't churn state (or storage) when the dashboard re-renders with
			// the same lesson — this runs from an effect on every mount.
			prev && prev.lessonId === lessonId && prev.studentName === studentName
				? prev
				: { lessonId, studentName, openedAt: Date.now() }
		);
	}, []);

	const clearOpenLesson = useCallback(() => setOpenLessonState(null), []);

	const value = useMemo(
		() => ({ openLesson, setOpenLesson, clearOpenLesson }),
		[openLesson, setOpenLesson, clearOpenLesson]
	);

	return <LessonSessionContext.Provider value={value}>{children}</LessonSessionContext.Provider>;
};

export const useLessonSession = () => useContext(LessonSessionContext);

export default LessonSessionContext;
